import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/shared/auth/useAuth'
import { formatMoneyValue } from '@/shared/lib/money'
import { formatInvoiceDay, type InvoiceDetail } from './invoiceApi'
import { INVOICE_PRINT_CSS, INVOICE_PRINT_ROOT } from './invoicePrint'

const cell: CSSProperties = { border: '0.2mm solid #999', padding: '1.5mm 2mm', verticalAlign: 'top' }
const head: CSSProperties = { ...cell, background: '#f0f0f0', fontWeight: 600, textAlign: 'start' }
const num: CSSProperties = { ...cell, textAlign: 'end', whiteSpace: 'nowrap' }

function Row({ label, value, strong }: { label: ReactNode; value: ReactNode; strong?: boolean }) {
  return (
    <tr style={strong ? { fontWeight: 700 } : undefined}>
      <td style={{ padding: '1mm 3mm' }}>{label}</td>
      <td style={{ padding: '1mm 0', textAlign: 'end', whiteSpace: 'nowrap' }}>{value}</td>
    </tr>
  )
}

export function InvoiceSheet({ invoice, copy }: { invoice: InvoiceDetail; copy: boolean }) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const [logoBroken, setLogoBroken] = useState(false)
  const company = invoice.company
  const companyName = company.name ?? user?.tenant.name ?? null
  const contacts = [company.address, company.phone, company.email, company.website].filter(
    (value): value is string => Boolean(value),
  )
  const customer = invoice.customer

  return (
    <div
      dir={i18n.dir()}
      style={{ fontFamily: 'Arial, "DejaVu Sans", sans-serif', fontSize: '10pt', color: '#000', background: '#fff' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8mm', alignItems: 'flex-start' }}>
        <div>
          {company.logoUrl && !logoBroken ? (
            <img
              src={company.logoUrl}
              alt=""
              onError={() => setLogoBroken(true)}
              style={{ display: 'block', maxHeight: '20mm', maxWidth: '60mm', marginBottom: '3mm' }}
            />
          ) : null}
          {companyName ? <div style={{ fontWeight: 700, fontSize: '13pt' }}>{companyName}</div> : null}
          {contacts.map((value) => (
            <div key={value}>
              <bdi>{value}</bdi>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'end' }}>
          <div style={{ fontSize: '20pt', fontWeight: 700, letterSpacing: '0.5mm' }}>{t('invoices.print.title')}</div>
          {copy ? (
            <div style={{ border: '0.4mm solid #000', padding: '0.5mm 3mm', fontWeight: 700, display: 'inline-block' }}>
              {t('invoices.print.copyMark')}
            </div>
          ) : null}
          <table style={{ marginInlineStart: 'auto', marginTop: '2mm' }}>
            <tbody>
              <Row label={t('invoices.print.number')} value={<bdi>{invoice.invoiceNo ?? t('invoices.draftNumber')}</bdi>} />
              <Row label={t('invoices.print.date')} value={<bdi>{formatInvoiceDay(invoice.invoiceDate)}</bdi>} />
              <Row label={t('invoices.print.due')} value={<bdi>{formatInvoiceDay(invoice.dueDate)}</bdi>} />
              <Row label={t('invoices.print.status')} value={t(`invoices.status.${invoice.status}`)} />
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8mm', marginTop: '6mm' }}>
        <div style={{ flex: 1, border: '0.2mm solid #999', padding: '3mm' }}>
          <div style={{ fontWeight: 700, marginBottom: '1mm' }}>{t('invoices.print.billTo')}</div>
          {customer.name ? <div>{customer.name}</div> : null}
          <div>
            {t('invoices.print.login')}: <bdi>{customer.login}</bdi>
          </div>
          {customer.customerId ? (
            <div>
              {t('invoices.print.customerId')}: <bdi>{customer.customerId}</bdi>
            </div>
          ) : null}
          {customer.phone ? (
            <div>
              {t('invoices.print.phone')}: <bdi>{customer.phone}</bdi>
            </div>
          ) : null}
          {customer.address ? (
            <div>
              {t('invoices.print.address')}: {customer.address}
            </div>
          ) : null}
        </div>
        <div style={{ flex: 1, border: '0.2mm solid #999', padding: '3mm' }}>
          <div>
            {t('invoices.print.branch')}: {invoice.branch.name} (<bdi>{invoice.branch.code}</bdi>)
          </div>
          {customer.center ? (
            <div>
              {t('invoices.print.center')}: {customer.center}
            </div>
          ) : null}
          {customer.visp ? (
            <div>
              {t('invoices.print.visp')}: {customer.visp}
            </div>
          ) : null}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6mm' }}>
        <thead>
          <tr>
            <th style={{ ...head, width: '8mm' }}>#</th>
            <th style={head}>{t('invoices.print.description')}</th>
            <th style={{ ...head, textAlign: 'end', width: '18mm' }}>{t('invoices.print.quantity')}</th>
            <th style={{ ...head, textAlign: 'end', width: '32mm' }}>{t('invoices.print.unitPrice')}</th>
            <th style={{ ...head, textAlign: 'end', width: '34mm' }}>{t('invoices.print.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr key={line.id}>
              <td style={num}>{line.lineNo}</td>
              <td style={cell}>
                <div>{line.description ?? t(`invoices.lineKind.${line.kind}`)}</div>
                <div style={{ fontSize: '8pt', color: '#555' }}>{t(`invoices.lineKind.${line.kind}`)}</div>
              </td>
              <td style={num}>{line.quantity}</td>
              <td style={num}>
                <bdi>{formatMoneyValue(line.unitPrice)}</bdi>
              </td>
              <td style={num}>
                <bdi>{formatMoneyValue(line.amount)}</bdi>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8mm', marginTop: '4mm' }}>
        <div style={{ flex: 1 }}>
          {invoice.notes ? (
            <>
              <div style={{ fontWeight: 700 }}>{t('invoices.print.notes')}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
            </>
          ) : null}
          {invoice.discount.amount > 0 && invoice.discountReason ? (
            <div style={{ marginTop: '2mm', fontSize: '9pt' }}>
              {t('invoices.print.discountReason')}: {invoice.discountReason}
            </div>
          ) : null}
        </div>
        <table style={{ minWidth: '75mm' }}>
          <tbody>
            <Row label={t('invoices.subtotal')} value={<bdi>{formatMoneyValue(invoice.subtotal)}</bdi>} />
            <Row label={t('invoices.discount')} value={<bdi>-{formatMoneyValue(invoice.discount)}</bdi>} />
            <Row label={t('invoices.total')} value={<bdi>{formatMoneyValue(invoice.total)}</bdi>} strong />
            <Row label={t('invoices.paid')} value={<bdi>{formatMoneyValue(invoice.paid)}</bdi>} />
            <Row label={t('invoices.outstanding')} value={<bdi>{formatMoneyValue(invoice.outstanding)}</bdi>} strong />
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '10mm', fontSize: '8.5pt', color: '#555', textAlign: 'center' }}>
        {t('invoices.print.footer')}
      </div>
    </div>
  )
}

export function InvoicePrintPortal({ invoice, copy }: { invoice: InvoiceDetail; copy: boolean }) {
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = INVOICE_PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={INVOICE_PRINT_ROOT}>
      <InvoiceSheet invoice={invoice} copy={copy} />
    </div>,
    document.body,
  )
}
